/**
 * marketingSample — the sample album the live design rails carry.
 *
 * MKTG-GAL14b. The marketing page renders the real gallery designs
 * and site templates live; until now they carried the app's five-shot
 * handoff album (galleries) and flat tonal blocks (sites), so every
 * card read as a wireframe. These are Rennan's own frames from the
 * rainy pine-forest wedding — the same set `marketingImages` serves —
 * as WEB-SIZED derivatives (1200px long edge, WebP q70, ~2.4 MB for
 * all 22) in `public/marketing/sample/`, because a rail of twenty-
 * three live cards must never pull the multi-MB originals.
 *
 * One album, two consumers:
 *   • SAMPLE_GALLERY — sixteen frames, mixed orientation, in an
 *     editorial order, for every gallery design on the page.
 *   • SITE_SAMPLE    — hero, project covers, reel cover, about
 *     portrait and journal covers for every site template.
 *
 * MKTG-GAL14c — the same album feeds the live demos (`/demo/gallery/
 * :design`, `/demo/site/:template`, pages/LiveDemo.tsx), so what a
 * visitor opens is exactly the card they clicked. The studio every
 * demo is signed by, the couple on each design and the photo-filled
 * site snapshot live here too, so the rails and the demo pages can
 * never drift apart.
 */
import { demoNameFor } from '@/components/gallery/design/GalleryDesignThumb';
import type { GalleryTemplateId } from '@/components/gallery/templates/types';
import { sampleSnapshot, type SiteMediaRef, type SiteSnapshot, type SiteTemplateId } from '@/components/sites/v2/types';

export interface SamplePhoto {
  id: string;
  url: string;
  width: number;
  height: number;
  name: string;
}

const DIR = '/marketing/sample';

const photo = (key: string, width: number, height: number, name: string): SamplePhoto => ({
  id: `sample-${key}`,
  url: `${DIR}/${key}.webp`,
  width,
  height,
  name,
});

/** Sixteen frames, editorial order: gate, walk, vows, table, dusk, details, party. */
export const SAMPLE_GALLERY: SamplePhoto[] = [
  photo('g01', 1200, 708, 'Under the umbrella'),
  photo('g02', 800, 1200, 'The path'),
  photo('g03', 800, 1200, 'Down the trail'),
  photo('g04', 1200, 666, 'Vows in the rain'),
  photo('g05', 800, 1200, 'Arm in arm'),
  photo('g06', 1200, 800, 'The table'),
  photo('g07', 800, 1200, 'Open road'),
  photo('g08', 938, 1200, 'Leis'),
  photo('g09', 1200, 539, 'Golden hour'),
  photo('g10', 960, 1200, 'Rings'),
  photo('g11', 1200, 800, 'I do'),
  photo('g12', 1200, 800, 'The party'),
  photo('g13', 800, 1200, 'Flower crown'),
  photo('g14', 800, 1200, 'The arch'),
  photo('g15', 1200, 542, 'Ceremony'),
  photo('g16', 1200, 732, 'From the back'),
];

/**
 * MKTG-GAL14e — a second album, so the rail isn't twenty-three views
 * of one wedding. Sixteen frames from an indoor portrait session
 * (flash, mixed orientation), same derivative convention as above:
 * WebP q70 off 1024px sources, no upscaling, ~1.0 MB for all sixteen,
 * in `public/marketing/highlights/`.
 *
 * Exactly one design carries it — Lookbook (08), whose layout is
 * built for editorial portrait sets and reads eleven verticals
 * without letterboxing. Every other design keeps SAMPLE_GALLERY.
 */
const HIGHLIGHTS_DIR = '/marketing/highlights';

const highlight = (key: string, width: number, height: number, name: string): SamplePhoto => ({
  id: `highlight-${key}`,
  url: `${HIGHLIGHTS_DIR}/${key}.webp`,
  width,
  height,
  name,
});

export const HIGHLIGHTS_GALLERY: SamplePhoto[] = [
  highlight('h01', 800, 1200, 'The packing'),
  highlight('h02', 800, 1200, 'Lampshade'),
  highlight('h03', 800, 1200, 'By the window'),
  highlight('h04', 800, 1200, 'The green chair'),
  highlight('h05', 1024, 683, 'Everything out'),
  highlight('h06', 800, 1200, 'Against the lockers'),
  highlight('h07', 800, 1200, 'Arms wide'),
  highlight('h08', 1024, 683, 'Floor level'),
  highlight('h09', 800, 1200, 'The red chair'),
  highlight('h10', 800, 1200, 'Lamplight'),
  highlight('h11', 800, 1200, 'Under the shade'),
  highlight('h12', 1024, 683, 'In the kitchen'),
  highlight('h13', 800, 1200, 'Countertop'),
  highlight('h14', 800, 1200, 'Pink room'),
  highlight('h15', 800, 1200, 'The doorway'),
  highlight('h16', 1024, 683, 'Last of it'),
];

/** Per-design album overrides. Empty since GALLERY-ONE: there is one
 *  gallery, and it carries the wedding. HIGHLIGHTS_GALLERY is still
 *  used elsewhere on the marketing page. */
const DESIGN_ALBUMS: Partial<Record<GalleryTemplateId, SamplePhoto[]>> = {};

export const galleryAlbumFor = (id: GalleryTemplateId): SamplePhoto[] =>
  DESIGN_ALBUMS[id] ?? SAMPLE_GALLERY;

const byKey = (key: string): SamplePhoto => SAMPLE_GALLERY.find((p) => p.id === `sample-${key}`) ?? SAMPLE_GALLERY[0];

/** What a site template is filled with on the marketing page. */
export const SITE_SAMPLE = {
  /** Home hero and the first work tile — one per template, in T1…T7 order. */
  heroes: [
    byKey('g01'), // T1 Grid — under the umbrella
    byKey('g16'), // T2 Full-Bleed — the ceremony, wide
    byKey('g02'), // T3 Editorial — the misty path
    byKey('g07'), // T4 Split — open road at dusk
    byKey('g09'), // T5 Minimal — golden hour at the table
    byKey('g11'), // T6 Brutalist — I do
    byKey('g05'), // T7 Magazine — arm in arm
  ],
  /** One cover per sample project, in SAMPLE_PROJECTS order (eight). */
  covers: [
    byKey('g02'),
    byKey('g07'),
    byKey('g06'),
    byKey('g09'),
    byKey('g11'),
    photo('s-reel', 1200, 800, 'The aisle'),
    byKey('g10'),
    photo('s-deck', 800, 1200, 'The deck'),
  ],
  /** The Reel page's poster. */
  reel: byKey('g16'),
  /** The About page's portrait. */
  about: photo('s-about', 800, 1200, 'Portrait'),
  /** Journal covers, in SAMPLE_POSTS order (three). */
  posts: [
    photo('s-post1', 800, 1200, 'Disco balls'),
    photo('s-post2', 793, 1200, 'From the window'),
    photo('s-post3', 960, 1200, 'Before the rain'),
  ],
  /** Extra frames for a project's own page. */
  media: [byKey('g03'), byKey('g05'), byKey('g08'), byKey('g12'), byKey('g13'), byKey('g14')],
} as const;

/** The studio every demo gallery and site on the marketing page is signed by. */
export const MARKETING_STUDIO = 'Wavesync';
export const MARKETING_BRAND = '#C9A16B';

/** The featured couple — Editorial reads this everywhere on the marketing page. */
// MKTG-SEP28 — was 'Rennan & Ethan' (Rennan).
export const MARKETING_COUPLE = 'Bey + Sean';
// GALLERY-ONE — one gallery, one featured couple. The Editorial and
// Lookbook special cases went with their designs.
export const marketingCoupleFor = (_id: GalleryTemplateId): string => MARKETING_COUPLE;

/** A sample photo as the site engine's media reference. */
const ref = (p: SamplePhoto): SiteMediaRef => ({
  id: p.id,
  kind: 'image',
  url: p.url,
  width: p.width,
  height: p.height,
  alt: p.name,
});

/**
 * The tonal sample snapshot, with photographs in every slot. Each
 * template gets its own hero and its own start into the covers, so
 * seven templates read as seven different sites, not one site seven
 * ways. `index` is the template's position in SITE_TEMPLATE_LIST.
 */
export function marketingSiteSnapshot(template: SiteTemplateId, index: number): SiteSnapshot {
  const hero = SITE_SAMPLE.heroes[index % SITE_SAMPLE.heroes.length];
  const covers = SITE_SAMPLE.covers;
  const base = sampleSnapshot(template, {
    heroImage: hero.url,
    reelCover: ref(SITE_SAMPLE.reel),
    aboutPortrait: ref(SITE_SAMPLE.about),
  });
  return {
    ...base,
    projects: base.projects.map((p, i) => ({
      ...p,
      cover: ref(covers[(i + index) % covers.length]),
      media: SITE_SAMPLE.media.slice(i % 3, (i % 3) + 3).map(ref),
    })),
    posts: base.posts.map((p, i) => ({
      ...p,
      cover: ref(SITE_SAMPLE.posts[i % SITE_SAMPLE.posts.length]),
    })),
  };
}
